import type { NextPage } from 'next';
import type { AppProps } from 'next/app';
import type { ReactElement, ReactNode } from 'react';
import React from 'react';
import { Provider, ReactReduxContext } from 'react-redux';
import { load as loadAnalytics } from '@core/analytics/analytics';
import ErrorBoundary from '@engage/error-reporting/ErrorBoundary';
import { makeAnalytics } from '@members/analytics/analytics';
import * as Env from '@members/env';
import {
  init as initDatadogReporting,
  isDatadogInitialized,
} from '@members/errors/datadog';
import Page from '@members/layout/Page';
import { setupErrorHandler } from '../_components/errorHandlers';
import { ErrorPage } from '../_components/ErrorPage';
import { GlobalComponents } from '../_components/GlobalComponents';
import GlobalContextProviders from '../_components/GlobalContextProviders';
import GlobalModals from '../_components/GlobalModals';
import { GlobalScripts } from '../_components/GlobalScripts';
import { GlobalStyles } from '../_components/GlobalStyles';
import { CustomHead } from '../_components/Head';
import { env } from '../_lib/env';
import { generateStore } from '../_lib/store';
import '../_lib/a11y';

type NextPageWithLayout = NextPage & {
  getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = Partial<AppProps> & {
  Component: NextPageWithLayout;
};

let retryCount = 0;
const maxRetries = 10;
// Fides
const waitForCMP = setInterval(() => {
  if (typeof window !== 'undefined' && window.Fides?.initialized) {
    if (!Env.isCypressTest() && !isDatadogInitialized()) {
      initDatadogReporting();
    }
    loadAnalytics({ key: env.segmentWriteKey }, makeAnalytics());

    clearInterval(waitForCMP);
  } else if (++retryCount >= maxRetries) {
    clearInterval(waitForCMP);
    if (retryCount === maxRetries) {
      console.error('Fides initialization failed after maximum retries');
    }
  }
}, 1000);

const errorHandler = setupErrorHandler(env);
const { store, client } = generateStore({
  env,
  errorHandler,
});
export default function AppWeb({ Component, pageProps }: AppProps) {
  return (
    <ErrorBoundary
      renderError={() => <ErrorPage />}
      errorReporter={errorHandler.reportError}
    >
      <CustomHead />
      <GlobalScripts />
      <Provider store={store} context={ReactReduxContext}>
        <div id="root">
          <GlobalContextProviders reduxStore={store} reduxClient={client}>
            <GlobalStyles />
            <GlobalComponents />
            <GlobalModals />
            <PageComponent Component={Component} pageProps={pageProps} />
          </GlobalContextProviders>
        </div>
      </Provider>
    </ErrorBoundary>
  );
}

const PageComponent = ({ Component, pageProps }: AppPropsWithLayout) => {
  const getLayout = Component.getLayout || (page => page);

  return <Page>{getLayout(<Component {...pageProps} />)}</Page>;
};
